import React, { useEffect, useState } from "react";
import { OfficeBuildingIcon, PlusIcon, RefreshIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import TableActions from "../../../../Components/v2/Table/TableActions";
import Table from "../../../../Components/v2/Table";
import Pagination from "../../../../Components/v2/Pagination";
import Button from "../../../../Components/v2/Button";
import FormDate from "../../../../Components/v2/Form/FormDate";
import FormError from "../../../../Components/v2/Form/FormError";
import SlideOver from "../../../../Components/v2/Modal/SlideOver";
import FixedAssetModal from "../../../../Components/v2/Accounting/FixedAssetModal";
import FixedAssetDetailModal from "../../../../Components/v2/Accounting/FixedAssetDetailModal";
import { ModalAlert } from "../../../../Components/ModalAlert";
import {
  addLocalNotification,
  clearBusinessFixedAsset,
  clearBusinessFixedAssetError,
  requestDeleteBusinessFixedAsset,
  requestListBusinessFixedAsset,
  requestRunDepreciation,
  useAuthState,
  useBusinessFixedAssetDispatch,
  useBusinessFixedAssetState,
  useNotificationDispatch,
} from "../../../../Context";
import { cancelAPIRequest, currencyFormat } from "../../../../Utils";
import { wibMonthRange, wibToday } from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const STATUS_STYLES = {
  ACTIVE: { badge: "bg-green-100 text-green-800", label: "fixedAssets.status.active" },
  FULLY_DEPRECIATED: {
    badge: "bg-blue-100 text-blue-800",
    label: "fixedAssets.status.fullyDepreciated",
  },
  DISPOSED: { badge: "bg-gray-100 text-gray-800", label: "fixedAssets.status.disposed" },
};

const AccountingFixedAssets = () => {
  const { t } = useTranslation();

  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(wibToday);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const dispatch = useBusinessFixedAssetDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user } = useAuthState();
  const {
    request,
    fixedAssets,
    fixedAssetsLoading,
    fixedAssetNextTokens,
    fixedAssetsError,
    deleteFixedAssetLoading,
    deleteFixedAssetError,
    deletedID,
    runDepreciationLoading,
    runDepreciationResult,
    runDepreciationError,
  } = useBusinessFixedAssetState();

  const loadList = (targetPage = 1, nextTokens) => {
    if (!user?.mainBusiness?.id) return;
    requestListBusinessFixedAsset(dispatch, {
      page: targetPage,
      limit: perPage,
      businessID: user.mainBusiness.id,
      nextTokens,
    });
  };

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearBusinessFixedAsset(dispatch);
      clearBusinessFixedAssetError(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      setPage(1);
      loadList(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, user?.mainBusiness?.id]);

  // Delete success
  useEffect(() => {
    if (deletedID) {
      setShowConfirmModal(false);
      addLocalNotification(notificationDispatch, {
        id: `delete-fixed-asset-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "fixedAssets.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID]);

  useEffect(() => {
    if (deleteFixedAssetError) setShowConfirmModal(false);
  }, [deleteFixedAssetError]);

  useEffect(() => {
    if (deleteFixedAssetError && !showConfirmModal) setShowErrorModal(true);
  }, [deleteFixedAssetError, showConfirmModal]);

  // Run-depreciation result. The Lambda posts per asset and keeps going after
  // one fails, so a run can succeed overall and still leave assets untouched --
  // report those instead of showing an unqualified success.
  useEffect(() => {
    if (runDepreciationResult) {
      setRunModalOpen(false);

      const failures =
        runDepreciationResult?.assets?.filter((item) => item.error) || [];

      addLocalNotification(notificationDispatch, {
        id: `run-depreciation-${Date.now()}`,
        type: failures.length ? "warning" : "success",
        title: failures.length ? "common.warning" : "common.success",
        body: failures.length
          ? t("fixedAssets.notifications.partialFailure", {
              count: failures.length,
              assets: failures
                .map((item) => item.name || item.assetID)
                .join(", "),
              interpolation: { escapeValue: false },
            })
          : t("fixedAssets.notifications.runDepreciationSuccess", {
              periods: runDepreciationResult.periodsPosted ?? 0,
              total: currencyFormat(
                runDepreciationResult.totalDepreciation ?? 0,
              ),
              interpolation: { escapeValue: false },
            }),
        showFull: true,
      });
      clearBusinessFixedAsset(dispatch);
      loadList(1);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runDepreciationResult]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (fixedAssetNextTokens[selectedPage] || selectedPage === 1) {
      loadList(selectedPage, fixedAssetNextTokens[selectedPage]);
    }
  };

  const handleAdd = () => {
    setSelectedAsset(null);
    setAssetModalOpen(true);
  };

  const handleViewDetail = (asset) => {
    setSelectedAsset(asset);
    setDetailModalOpen(true);
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setAssetModalOpen(true);
  };

  const handleEditFromDetail = (asset) => {
    setDetailModalOpen(false);
    setSelectedAsset(asset);
    setAssetModalOpen(true);
  };

  const handleDelete = (asset) => {
    setSelectedAsset(asset);
    setShowConfirmModal(true);
  };

  const onDelete = () => {
    if (selectedAsset?.id) {
      requestDeleteBusinessFixedAsset(dispatch, {
        id: selectedAsset.id,
        businessID: user?.mainBusiness?.id,
      });
    }
  };

  const openRunModal = () => {
    clearBusinessFixedAssetError(dispatch);
    setRunModalOpen(true);
  };

  const closeRunModal = () => {
    clearBusinessFixedAssetError(dispatch);
    setRunModalOpen(false);
  };

  const handleRunDepreciation = () => {
    if (!user?.mainBusiness?.id) return;
    requestRunDepreciation(dispatch, {
      businessID: user.mainBusiness.id,
      periodEnd: wibMonthRange(periodEnd).endDate,
    });
  };

  const handleClose = () => {
    cancelAPIRequest(request);
    setAssetModalOpen(false);
    setSelectedAsset(null);
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
    setSelectedAsset(null);
  };

  const getDeleteDescription = () => {
    if (!selectedAsset) return null;
    return t("fixedAssets.modal.description.deleteItem", {
      name: selectedAsset.name,
      cost: currencyFormat(selectedAsset.acquisitionCost),
      interpolation: { escapeValue: false },
    });
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {t("fixedAssets.label")}
              </h2>
            </div>
            <div className="mt-4 flex gap-2 md:mt-0 md:ml-4">
              <button
                onClick={openRunModal}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshIcon className="h-5 w-5 mr-2" />
                {t("fixedAssets.actions.runDepreciation")}
              </button>
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t("fixedAssets.actions.add")}
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <Table
            isLoading={fixedAssetsLoading}
            error={fixedAssetsError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "fixedAssets.fields.name",
                accessor: (asset) => (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <OfficeBuildingIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {asset.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {asset.assetAccountCode}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                header: "fixedAssets.fields.acquisitionCost",
                accessor: (asset) => currencyFormat(asset.acquisitionCost),
                align: "right",
              },
              {
                header: "fixedAssets.fields.accumulatedDepreciation",
                accessor: (asset) =>
                  currencyFormat(asset.accumulatedDepreciation),
                align: "right",
              },
              {
                header: "fixedAssets.fields.netBookValue",
                accessor: (asset) =>
                  currencyFormat(
                    (asset.acquisitionCost || 0) -
                      (asset.accumulatedDepreciation || 0),
                  ),
                align: "right",
              },
              {
                header: "fixedAssets.fields.status",
                accessor: (asset) => {
                  const style =
                    STATUS_STYLES[asset.status] || STATUS_STYLES.ACTIVE;
                  return (
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${style.badge}`}
                    >
                      {t(style.label)}
                    </span>
                  );
                },
              },
              {
                header: "common.actions.label",
                accessor: (asset, index) => {
                  // A disposed asset is frozen, and one with posted
                  // depreciation can still be renamed but never removed --
                  // the Lambda refuses both, so don't offer the action.
                  const canEdit = asset.status !== "DISPOSED";
                  const canDelete =
                    asset.status !== "DISPOSED" &&
                    Number(asset.accumulatedDepreciation || 0) === 0;

                  return (
                    <TableActions
                      position={
                        index === fixedAssets.length - 1 ? "top" : "bottom"
                      }
                      actions={[
                        {
                          label: t("common.view"),
                          onClick: () => handleViewDetail(asset),
                        },
                        ...(canEdit
                          ? [
                              {
                                label: t("common.edit"),
                                onClick: () => handleEdit(asset),
                              },
                            ]
                          : []),
                        ...(canDelete
                          ? [
                              {
                                label: t("common.delete"),
                                onClick: () => handleDelete(asset),
                                variant: "danger",
                              },
                            ]
                          : []),
                      ]}
                    />
                  );
                },
                align: "right",
              },
            ]}
            data={fixedAssets}
          />
        </div>

        <Pagination
          nextTokens={fixedAssetNextTokens}
          currentPage={page}
          isLoading={fixedAssetsLoading}
          onPageChange={onPageChange}
          perPage={perPage}
          onPerPageChange={setPerPage}
        />

        {assetModalOpen && (
          <FixedAssetModal
            open={assetModalOpen}
            onClose={handleClose}
            asset={selectedAsset}
          />
        )}

        {detailModalOpen && (
          <FixedAssetDetailModal
            open={detailModalOpen}
            onClose={handleCloseDetail}
            asset={selectedAsset}
            onEdit={handleEditFromDetail}
            onDisposed={() => loadList(page, fixedAssetNextTokens[page])}
          />
        )}

        {/* Run depreciation modal */}
        {runModalOpen && (
          <SlideOver
            open={runModalOpen}
            onClose={closeRunModal}
            title="fixedAssets.modal.title.runDepreciation"
            footer={
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeRunModal}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  loading={runDepreciationLoading}
                  onClick={handleRunDepreciation}
                >
                  {t("fixedAssets.actions.runDepreciation")}
                </Button>
              </div>
            }
          >
            <div className="space-y-4 mb-6">
              <FormError error={runDepreciationError} />
              <p className="text-sm text-gray-600">
                {t("fixedAssets.modal.description.runDepreciation")}
              </p>
              <FormDate
                label="fixedAssets.fields.periodEnd"
                selected={periodEnd}
                onChange={(date) => setPeriodEnd(date)}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                // The server books depreciation only up to this month.
                maxDate={new Date()}
              />
            </div>
          </SlideOver>
        )}

        {/* Delete confirm */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={setShowConfirmModal}
          title="fixedAssets.modal.title.delete"
          description="fixedAssets.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          loadingButton={deleteFixedAssetLoading}
          disabledButton={deleteFixedAssetLoading}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
          }}
        />

        {/* Error modal */}
        {deleteFixedAssetError && (
          <ErrorDialog
            open={showErrorModal}
            error={deleteFixedAssetError}
            onClose={() => {
              setShowErrorModal(false);
              clearBusinessFixedAssetError(dispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingFixedAssets;
